import { css } from '@emotion/css';
import { Box, Flex, Group, MultiSelect, Stack, Title } from '@mantine/core';
import { useGroup } from 'app/Private/GroupGate';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';

import { useGroupTenants } from './consolidation';
import { GroupAgedBalance } from './GroupAgedBalance';
import { GroupBalanceHero } from './GroupBalanceHero';
import { GroupByCompany } from './GroupByCompany';
import { GroupCei } from './GroupCei';
import { GroupComputedAt } from './GroupComputedAt';
import { GroupDso } from './GroupDso';
import { GroupTour } from './GroupTour';

export const GroupView = () => {
  const { t } = useTranslation();
  const group = useGroup();
  const groupTenants = useGroupTenants();
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const companyCount = selectedCompanies.length || groupTenants.length;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" gap="md">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={2}>{group.name}</Title>
            <GroupTour companyCount={groupTenants.length} />
          </Group>
          <GroupComputedAt selectedCompanies={selectedCompanies} />
        </Stack>
        <MultiSelect
          searchable
          clearable
          clearButtonProps={{
            'aria-hidden': false,
            'aria-label': t(i18nKeys.GROUP.CLEAR_FILTER),
            tabIndex: 0,
          }}
          placeholder={t(i18nKeys.GROUP.FILTER_COMPANIES)}
          data={groupTenants.map(({ subdomain, companyName }) => ({
            value: subdomain,
            label: companyName,
          }))}
          value={selectedCompanies}
          onChange={setSelectedCompanies}
          wrapperProps={{ 'data-tour': 'group-company-filter' }}
          w={320}
        />
      </Group>
      <GroupBalanceHero selectedCompanies={selectedCompanies} />
      <Flex gap="md" wrap="wrap" align="stretch" className={styles.cardRow}>
        <Box flex="1 1 420px" miw={0} data-tour="group-aged-balance">
          <GroupAgedBalance selectedCompanies={selectedCompanies} />
        </Box>
        {companyCount !== 1 && (
          <Box
            flex="1 1 420px"
            miw={0}
            className={styles.sizedByNeighbour}
            data-tour="group-by-company"
          >
            <GroupByCompany selectedCompanies={selectedCompanies} />
          </Box>
        )}
      </Flex>
      <Flex gap="md" wrap="wrap" align="stretch" data-tour="group-performance">
        <Box flex="1 1 420px" miw={0}>
          <GroupCei selectedCompanies={selectedCompanies} />
        </Box>
        <Box flex="1 1 420px" miw={0}>
          <GroupDso selectedCompanies={selectedCompanies} />
        </Box>
      </Flex>
    </Stack>
  );
};

const styles = {
  cardRow: css`
    container-type: inline-size;
  `,
  sizedByNeighbour: css`
    @container (min-width: 856px) {
      contain: size;
    }
  `,
};
